/**
 * Stripe Payment Service
 * Simple payment processing with Stripe
 */

import api from './api';

// Lazy load Stripe to avoid compilation errors; catch script load failures (network, ad blocker, etc.)
let loadStripe;
let stripePromise;

try {
  const stripeJs = require('@stripe/stripe-js');
  loadStripe = stripeJs.loadStripe;
  const key = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_your_key_here';
  stripePromise = loadStripe(key).catch((err) => {
    console.warn('Stripe.js failed to load:', err && err.message ? err.message : err);
    return null;
  });
} catch (error) {
  console.warn('Stripe dependencies not installed yet. Please run: npm install @stripe/stripe-js @stripe/react-stripe-js');
  stripePromise = Promise.resolve(null);
}

class StripeService {

  /**
   * Create a payment intent for subscription
   * @param {Object} paymentData - Payment information
   * @returns {Promise} Payment intent response
   */
  async createPaymentIntent(paymentData) {
    try {
      return await api.post('/payments/create-intent', paymentData);
    } catch (error) {
      console.error('Error creating payment intent:', error);
      throw error;
    }
  }

  /**
   * Process payment with Stripe Elements
   * @param {Object} stripe - Stripe instance
   * @param {Object} elements - Stripe elements
   * @param {string} clientSecret - Payment intent client secret
   * @param {Object} billingDetails - Customer billing information
   * @returns {Promise} Payment result
   */
  async processPayment(stripe, elements, clientSecret, billingDetails) {
    try {
      const cardElement = elements.getElement('card');
      
      if (!cardElement) {
        throw new Error('Card element not found');
      }

      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: billingDetails
        }
      });

      if (error) {
        throw new Error(error.message);
      }

      return { success: true, paymentIntent };
    } catch (error) {
      console.error('Error processing payment:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Create a subscription
   * @param {Object} subscriptionData - Subscription details
   * @returns {Promise} Subscription response
   */
  async createSubscription(subscriptionData) {
    try {
      return await api.post('/payments/create-subscription', subscriptionData);
    } catch (error) {
      console.error('Error creating subscription:', error);
      throw error;
    }
  }

  /**
   * Update payment method
   * @param {Object} paymentMethodData - New payment method details
   * @returns {Promise} Update response
   */
  async updatePaymentMethod(paymentMethodData) {
    try {
      return await api.post('/payments/update-payment-method', paymentMethodData);
    } catch (error) {
      console.error('Error updating payment method:', error);
      throw error;
    }
  }

  /**
   * Get Stripe instance
   * @returns {Promise} Stripe instance
   */
  async getStripe() {
    return await stripePromise;
  }
}

const stripeService = new StripeService();
export default stripeService;
